import { FormApi } from "final-form";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "src/store";
import { PrimaryButton } from "src/ui/shared/CustomButton";
import { Modal } from "src/ui/shared/Modal/Modal";

import { updateGroup, updateGroupParent } from "@dashboard/devices/store/index";
import { useWorkspaceFeature } from "@dashboard/workspaces/api/helpers";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { TextField } from "@shared/form/TextField";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { GroupOverviewData } from "../GroupPage.utils";
import { selectGroupChildrenIds, selectGroups } from "../store/selectors";
import { getNestedGroupsWithIndentation, useParentGroupSelection, validateGroupForm } from "../utils";

interface FormValues {
    name: string;
    description: string;
    parentId: number | null;
}

interface Props {
    group: GroupOverviewData;
    open: boolean;
    onClose: () => void;
}

const EditGroupDialog = (props: Props) => {
    const { t } = useTranslation(["modals", "common"]);
    const dispatch = useDispatch();

    const groups = useSelector(selectGroups);
    const childrenGroupIds = useSelector((state) => selectGroupChildrenIds(state, props.group.id));
    const availableGroups = groups.filter((group) => ![...childrenGroupIds, props.group.id].includes(group.id));

    const noParentGroupValue = 0;
    const groupOptions = getNestedGroupsWithIndentation(availableGroups);

    const groupNestingLimit = useWorkspaceFeature("GroupNestingLimit");

    const validate = (values: FormValues) => validateGroupForm(values, groups, groupNestingLimit, props.group.id);
    const { isLimitReached, handleParentGroupChange } = useParentGroupSelection(groups, groupNestingLimit);

    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        try {
            const initialValues = form.getState().initialValues;
            const parentIdChanged = values.parentId !== initialValues?.parentId;

            // Set parentId to null if "No Parent Group" is selected
            const parentId = values.parentId === noParentGroupValue ? null : values.parentId;
            if (parentIdChanged) {
                await dispatch(updateGroupParent({ id: props.group.id, parentId })).unwrap();
            }

            const nameChanged = values.name !== initialValues?.name;
            const descriptionChanged = values.description !== initialValues?.description;
            if (nameChanged || descriptionChanged) {
                await dispatch(updateGroup({ id: props.group.id, name: values.name, description: values.description })).unwrap();
            }

            showSuccessToast(t("modals:editGroup.toast.groupUpdatedSuccess"));
            props.onClose();
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("modals:editGroup.toast.groupUpdatedError"));
        }
    };

    return (
        <Form<FormValues>
            validate={validate}
            onSubmit={onSubmit}
            initialValues={{
                name: props.group.name,
                description: props.group.description,
                parentId: props.group.parentId || noParentGroupValue,
            }}
            render={({ handleSubmit, pristine, form }) => (
                <form onSubmit={handleSubmit}>
                    <Modal
                        open={props.open}
                        onClose={props.onClose}
                        title={t("modals:editGroup.title")}
                        endButton={
                            <PrimaryButton
                                onClick={form.submit}
                                disabled={pristine || isLimitReached}
                                tooltipProps={{
                                    title: isLimitReached ? t("modals:editGroup.tooltip.groupNestingLimitReached") : "",
                                }}
                            >
                                {t("common:button.save")}
                            </PrimaryButton>
                        }
                    >
                        <TextField name="name" label={t("modals:editGroup.input.name.caption")} required />
                        <TextField name="description" label={t("common:label.description")} />
                        <ControlledSelectField
                            name="parentId"
                            label={t("modals:editGroup.input.parentGroup.caption")}
                            options={groupOptions}
                            onChange={handleParentGroupChange}
                            disableSorting
                            disableClearable={false}
                        />
                    </Modal>
                </form>
            )}
        />
    );
};

export default EditGroupDialog;
